import React from "react"
import PropTypes from "prop-types"
import Grid from "@mui/material/Grid"
import PhoneIcon from "@mui/icons-material/PhoneIphone"
import PersonIcon from "@mui/icons-material/TagFaces"
import CustomizeIcon from "@mui/icons-material/LooksOne"
import PickupIcon from "@mui/icons-material/TransferWithinAStation"
import CertifiedIcon from "@mui/icons-material/CheckCircle"
import AvailIcon from "@mui/icons-material/DateRange"
import CarIcon from "@mui/icons-material/DirectionsCar"
import SchoolIcon from "@mui/icons-material/School"
import ChatIcon from "@mui/icons-material/ChatBubble"

import Section from "../template-parts/product-marketing/Section"
import IconTextBlock from "../template-parts/product-marketing/IconTextBlock"

import { NUM_STUDENTS } from "../../config"

const features = {
  generic: [
    {
      head: "Certified driving instructors",
      text: "Every instructor is certified and licensed and has completed an intensive training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Online booking, secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Pickup and drop off available in most states",
      text: "We come to your house, school or work—and can even drop you off at a convenient location within 5-10 minutes of your pickup address, or meet in convenient public locations",
      icon: <PickupIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—our instructors customize every lesson to your precise needs",
      icon: <CustomizeIcon />,
    },
    {
      head: "Transparent availability",
      text: "Our online booking system shows you all available time slots, so you can book packages knowing when we're available",
      icon: <AvailIcon />,
    },
    {
      head: "Driving Test coaching",
      text: "We teach you the skills you need to be a great driver, and also prepare you for your road test, so you can test with confidence",
      icon: <CarIcon />,
    },
    {
      head: "Dedicated support team",
      text: "Our highly trained customer support staff can answer any question—from scheduling help to the licensing process, we're here for you",
      icon: <ChatIcon />,
    },
  ],
  ca: [
    {
      head: "Pickup and drop off available",
      text: "We come to your house, school or work—and can even drop you off at a convenient location within 5-10 minutes of your pickup address",
      icon: <PickupIcon />,
    },
    {
      head: "DMV-certified driving instructors",
      text: "Every instructor is certified and licensed by the DMV and has completed an intensive training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Online booking, secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Transparent availability",
      text: "Our online booking system shows you all available time slots, so you can book packages knowing when we're available",
      icon: <AvailIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—our instructors customize every lesson to your precise needs",
      icon: <CustomizeIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "DMV Test coaching",
      text: "We teach you the skills you need to be a great driver, and also prepare you for your road test, so you can test with confidence",
      icon: <CarIcon />,
    },
  ],
  co: [
    {
      head: "Pickup and drop off available",
      text: "We meet you at your house, school or work - no need to come to our office",
      icon: <PickupIcon />,
    },
    {
      head: "State-certified driving instructors",
      text: "Every instructor is certified and licensed by the Colorado DMV and has completed an intensive training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Online booking, secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—our instructors customize every lesson to your precise needs",
      icon: <CustomizeIcon />,
    },
    {
      head: "Transparent availability",
      text: "Our online booking system shows you all available time slots, so you can book packages knowing when we're available",
      icon: <AvailIcon />,
    },
    {
      head: "Largest driving school in the country",
      text: "We have taught tens of thousands of lessons across the United States, and our local Colorado instructors are the best in the business",
      icon: <CarIcon />,
    },
  ],
  il: [
    {
      head: "Certified driving instructors",
      text: "Every instructor is certified and licensed and has completed an intensive training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Online booking, secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Premium classroom experience",
      text: "Our classroom course is taught by experienced driving instructors who are committed to an engaging classroom experience",
      icon: <PickupIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—our instructors customize every lesson to your precise needs",
      icon: <CustomizeIcon />,
    },
    {
      head: "Transparent availability",
      text: "Our online booking system shows you all available time slots, so you can book packages knowing when we're available",
      icon: <AvailIcon />,
    },
    {
      head: "Driving Test coaching",
      text: "We teach you the skills you need to be a great driver, and also prepare you for your road test, so you can test with confidence",
      icon: <CarIcon />,
    },
    {
      head: "Take your Driving Test with Us",
      text: "Skip the long waits at the DMV and take your driving test with a Coastline instructor in a Coastline vehicle.",
      icon: <CarIcon />,
    },
  ],
  pa: [
    {
      head: "Certified driving instructors",
      text: "Every instructor is certified and licensed and has completed an intensive training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Online booking, secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Premium classroom experience",
      text: "Our classroom course is taught by experienced driving instructors who are committed to an engaging classroom experience",
      icon: <PickupIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—our instructors customize every lesson to your precise needs",
      icon: <CustomizeIcon />,
    },
    {
      head: "Transparent availability",
      text: "Our online booking system shows you all available time slots, so you can book packages knowing when we're available",
      icon: <AvailIcon />,
    },
    {
      head: "Driving Test coaching",
      text: "We teach you the skills you need to be a great driver, and also prepare you for your road test, so you can test with confidence",
      icon: <CarIcon />,
    },
    {
      head: "Take your Driving Test with Us",
      text: "Skip the long waits at PennDOT and take your driving test with a Coastline instructor in a Coastline vehicle.",
      icon: <CarIcon />,
    },
  ],
  in: [
    {
      head: "BMV-certified driving instructors",
      text: "Every instructor is certified and licensed by the BMV and has completed an intensive training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Online booking, secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—every lesson is customized to your precise needs",
      icon: <CustomizeIcon />,
    },
    {
      head: "BMV Test coaching",
      text: "We teach you the skills you need to be a great driver, and also prepare you for your road test, so you can test with confidence",
      icon: <CarIcon />,
    },
    {
      head: "Post-Lesson Report Included",
      text: "At the end of your lesson, your instructor fills out a post-lesson report that shows the skills you have mastered, and those that need more practice, so you can maximize your practice time between lessons.",
      icon: <SchoolIcon />,
    },
  ],
  mo: [
    {
      head: "Highly trained driving instructors",
      text: "Every instructor has completed an intensive, nation-leading training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—our instructors customize every lesson to your precise needs",
      icon: <CustomizeIcon />,
    },
    {
      head: "Safe and confident for life",
      text: "We teach you the skills you need to be a great driver in the real world - our mission is to eradicate car crashes, and that starts with how we work with you",
      icon: <CarIcon />,
    },
  ],
  nv: [
    {
      head: "Highly trained driving instructors",
      text: "Every instructor has completed an intensive, nation-leading training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—our instructors customize every lesson to your precise needs",
      icon: <CustomizeIcon />,
    },
    {
      head: "Safe and confident for life",
      text: "We teach you the skills you need to be a great driver in the real world - our mission is to eradicate car crashes, and that starts with how we work with you",
      icon: <CarIcon />,
    },
  ],
  tx: [
    {
      head: "Certified driving instructors",
      text: "Every instructor is certified and licensed and has completed an intensive training course",
      icon: <CertifiedIcon />,
    },
    {
      head: "Cutting-edge technology",
      text: "Secure payments and digital lesson reports—we leverage the latest tech to provide an experience you will love",
      icon: <PhoneIcon />,
    },
    {
      head: "Customers first",
      text: "Our instructors don't run errands or make students pick up and drop off other students—your time behind the wheel is your time",
      icon: <PersonIcon />,
    },
    {
      head: "Customized lesson plans",
      text: "Our driving lessons aren't for everyone, they're for you—our instructors customize every lesson to your precise needs",
      icon: <CustomizeIcon />,
    },
  ],
}

const FeatureList = ({ USState }) => {
  const stateFeatures =
    USState && features[USState] ? features[USState] : features.generic

  return (
    <Section
      head={`Over ${NUM_STUDENTS} Students Have Chosen Coastline Academy`}
      subhead="See why 98% of students across the country rate their driving lesson with us 5-stars."
    >
      <Grid container spacing={5}>
        {stateFeatures.map((feature) => (
          <Grid item xs={12} md={6} key={feature.head}>
            <IconTextBlock
              head={feature.head}
              text={feature.text}
              link={feature.link}
              linkText={feature.linkText}
              icon={feature.icon}
            />
          </Grid>
        ))}
      </Grid>
    </Section>
  )
}

FeatureList.propTypes = {
  USState: PropTypes.string,
}

FeatureList.defaultProps = {
  USState: "ca",
}

export default FeatureList
